
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useUserRole } from "@/hooks/useUserRole";
import { Separator } from "@/components/ui/separator";
import { DocumentUploader } from "./document-management/DocumentUploader";
import { DocumentList } from "./document-management/DocumentList";
import { useEffect, useState } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { logger } from "@/services/LoggingService";

const COMPONENT = 'FundDocuments';

interface FundDocumentsProps {
  fundId: string;
}

export function FundDocuments({ fundId }: FundDocumentsProps) {
  const { userRole } = useUserRole();
  const [localDocuments, setLocalDocuments] = useState<any[]>([]);
  const canManageDocuments = userRole === 'sys_admin';

  const { data: documents, isLoading, refetch } = useQuery({
    queryKey: ['fund-documents', fundId],
    queryFn: async () => {
      try {
        const { data, error } = await supabase
          .from('fund_documents')
          .select('*')
          .eq('fund_id', fundId)
          .order('created_at', { ascending: false });

        if (error) {
          logger.error(COMPONENT, 'Error fetching documents:', { error, fundId });
          throw error;
        }
        
        return data || [];
      } catch (error) {
        logger.error(COMPONENT, 'Query error:', { error, fundId });
        throw error;
      }
    },
  });

  useEffect(() => {
    if (documents) {
      setLocalDocuments(documents);
    }
  }, [documents]);

  const handleUploadComplete = async (newDoc: any) => {
    setLocalDocuments(prev => [newDoc, ...prev]);
    await refetch(); // Refresh the document list to ensure consistency
  };

  const handleDocumentDeleted = async (deletedId: string) => {
    setLocalDocuments(prev => prev.filter(doc => doc.id !== deletedId));
    await refetch(); // Refresh the document list to ensure consistency
  };

  if (isLoading) {
    return (
      <>
        <Separator className="my-6" />
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Skeleton className="h-8 w-24" />
          </div>
          <div className="space-y-2">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Separator className="my-6" />
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium">Documents</h3>
          {canManageDocuments && (
            <DocumentUploader 
              fundId={fundId} 
              onUploadComplete={handleUploadComplete}
            />
          )}
        </div>

        <DocumentList 
          documents={localDocuments}
          canManageDocuments={canManageDocuments}
          onDocumentDeleted={handleDocumentDeleted}
        />
      </div>
    </>
  );
}
